import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import type { InboxThreadFilters } from '../../types/inbox';
import { describeActiveFilters } from '../../lib/inboxView';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { previewBulkResolve } from './api';

// Community Inbox Part D (2026-07-19) — "resolve all showing". Resolves
// EVERYTHING matching the active filters (not just the loaded page), which is
// the cleanup path for the historical review backlog. Bulk + hard to undo, so
// it always confirms with the exact COUNT and a plain-English filter summary.

export function BulkResolveDialog({
  open,
  onOpenChange,
  filters,
  accountNameById,
  onConfirm,
  resolving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The feed's ACTIVE filters (already including the kind segment).
  filters: InboxThreadFilters;
  accountNameById: Map<string, string>;
  onConfirm: () => void;
  resolving: boolean;
}) {
  // Count comes from the server using the same filter path as the feed, so it
  // can't drift from what's actually resolved.
  const preview = useQuery({
    queryKey: ['community-bulk-resolve-preview', filters],
    queryFn: () => previewBulkResolve(filters),
    enabled: open,
    staleTime: 0,
  });

  const count = preview.data?.count ?? 0;
  const cap = preview.data?.cap ?? 0;
  const capped = count > cap;
  const summary = describeActiveFilters(filters, {
    accountName: (id) => accountNameById.get(id),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Resolve everything showing?</DialogTitle>
          <DialogDescription>
            This resolves every unresolved item matching the current filters — not just the ones
            loaded on screen. Each change is recorded in the audit log.
          </DialogDescription>
        </DialogHeader>

        {preview.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
            <Spinner className="h-4 w-4" />
            Counting matching items…
          </div>
        ) : preview.isError ? (
          <p className="py-4 text-sm text-rose-700">Couldn't count the matching items.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-800">
              <span className="text-2xl font-semibold text-slate-900">{count}</span>{' '}
              {count === 1 ? 'item' : 'items'} will be marked <strong>Resolved</strong>.
            </p>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                Current filters
              </p>
              {summary.length === 0 ? (
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                  No filters are set — this resolves the entire inbox.
                </p>
              ) : (
                <ul className="list-inside list-disc text-sm text-slate-700">
                  {summary.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              )}
            </div>

            {capped && (
              <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                Only the first {cap} will be resolved in this run — repeat the action to clear the
                rest.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={resolving || count === 0}>
            {resolving ? <Spinner className="h-3.5 w-3.5" /> : null}
            Resolve {count > 0 ? count : ''} {count === 1 ? 'item' : 'items'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
